/**
 * Cross-Sell API client.
 *
 * ARCHITECTURE RULE (janus-frontend/CLAUDE.md): the frontend NEVER calls a
 * backend service directly. Every call goes through `api` (apiClient) whose
 * baseURL is `/api/v1`, i.e. the BFF. The Fase 2 BFF proxies cross-sell routes
 * to janus-sales-api (with the SSE passthrough).
 *
 * Endpoints consumed (all under `/api/v1/cross-sell`, BFF Fase 2):
 *   POST   /imports                       create import (multipart, ?force=true)
 *   GET    /imports                       list recent imports
 *   GET    /imports/:id                   job detail (preview/progress snapshot)
 *   POST   /imports/:id/confirm           LGPD consent + start processing
 *   POST   /imports/:id/cancel            cancel a non-terminal job
 *   GET    /imports/:id/stream            per-job SSE (consumed via EventSource)
 *   GET    /events/stream                 global per-tenant SSE (EventSource)
 *   GET    /accounts/:id/whitespace       account whitespace (has/eligible/...)
 *   GET    /products/:id/whitespace       product whitespace (accounts)
 *   POST   /exclusions                    exclude a product from an account
 *   DELETE /exclusions                    re-include (un-exclude)
 *
 * Campaigns (F-CS-03/04/05, TECH_SPEC §5.3/§5.4 — BFF proxy, contract-stable):
 *   POST   /campaigns                     create draft campaign
 *   POST   /campaigns/preview-reach       async reach estimate (accounts/contacts)
 *   GET    /campaigns                     list campaigns
 *   GET    /campaigns/:id                 campaign detail
 *   POST   /campaigns/:id/activate        snapshot members + enqueue (202)
 *   POST   /campaigns/:id/pause           pause an active campaign
 *   GET    /campaigns/:id/members         cursor-paginated members (by status)
 *   GET    /campaigns/:id/funnel          funnel counters + conversion rates
 *   POST   /campaign-members/:id/outcome  SDR submits the call outcome (202)
 *
 * SSE is opened with `EventSource` (cookie auth, `withCredentials: true`) — NOT
 * through axios — see the hooks. This module only exposes the URL builders.
 */
import api from '../../../shared/utils/api';
import type {
  AccountWhitespace,
  ActivateCampaignResult,
  Campaign,
  CampaignListItem,
  CampaignMembersPage,
  CampaignMemberStatus,
  CatalogImportPreview,
  CatalogImportResult,
  ConsentPayload,
  CreateCampaignPayload,
  CreateExclusionPayload,
  FunnelData,
  ImportJob,
  ImportJobListItem,
  PreviewReach,
  PreviewReachPayload,
  ProductWhitespace,
  RankedOffer,
  SubmitOutcomePayload,
  SubmitOutcomeResult,
} from '../types';

/** The base path all cross-sell SSE EventSources are built from. */
export const CROSS_SELL_SSE_BASE = '/api/v1/cross-sell';

/**
 * Append the JWT as an `?access_token=` query param to an SSE URL.
 *
 * `EventSource` cannot send an `Authorization` header, so SSE requests would
 * otherwise reach the BFF unauthenticated and 401. The BFF accepts the token
 * via query param for SSE routes. We pull it from `localStorage` (same key the
 * axios client uses) and URL-encode it. If there is no token we return the URL
 * untouched (cookie auth still applies via `withCredentials`). The token is
 * never logged.
 */
export const withSseToken = (url: string): string => {
  let token: string | null = null;
  try {
    token = localStorage.getItem('accessToken');
  } catch {
    token = null;
  }
  if (!token) return url;
  const sep = url.includes('?') ? '&' : '?';
  return `${url}${sep}access_token=${encodeURIComponent(token)}`;
};

export const crossSellApi = {
  /* ── Imports ──────────────────────────────────────────────────────────── */

  /**
   * Create an import job from a file. `force=true` reprocesses a previously
   * seen hash (PRD_F1_DELTA §3.3). On a duplicate hash with force=false the BFF
   * returns 409 (ADR-003) — the caller inspects the AxiosError to render the
   * "Ver existente / Forçar" modal.
   */
  createImport: async (file: File, opts?: { force?: boolean }): Promise<ImportJob> => {
    const form = new FormData();
    form.append('file', file);
    const { data } = await api.post<ImportJob>('/cross-sell/imports', form, {
      params: opts?.force ? { force: true } : undefined,
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return data;
  },

  listImports: async (): Promise<ImportJobListItem[]> => {
    const { data } = await api.get<ImportJobListItem[] | { data: ImportJobListItem[] }>(
      '/cross-sell/imports',
    );
    return Array.isArray(data) ? data : (data?.data ?? []);
  },

  getImport: async (jobId: string): Promise<ImportJob> => {
    const { data } = await api.get<ImportJob>(`/cross-sell/imports/${jobId}`);
    return data;
  },

  /**
   * Confirm the import: registers LGPD consent (when decisores > 0) and moves
   * the job from `preview_ready` → `confirmed`. NEVER optimistic (UX §5.2).
   */
  confirmImport: async (jobId: string, consent: ConsentPayload): Promise<ImportJob> => {
    const { data } = await api.post<ImportJob>(`/cross-sell/imports/${jobId}/confirm`, {
      consent,
    });
    return data;
  },

  cancelImport: async (jobId: string): Promise<ImportJob> => {
    const { data } = await api.post<ImportJob>(`/cross-sell/imports/${jobId}/cancel`, {});
    return data;
  },

  /** URL for the per-job SSE EventSource (cookie + query-token auth). */
  importStreamUrl: (jobId: string): string =>
    withSseToken(`${CROSS_SELL_SSE_BASE}/imports/${jobId}/stream`),

  /** URL for the global per-tenant SSE EventSource (cookie + query-token auth). */
  globalStreamUrl: (): string => withSseToken(`${CROSS_SELL_SSE_BASE}/events/stream`),

  /* ── Templates (Onda 3 / XS-LOAD) ─────────────────────────────────────── */

  /**
   * Download the commercial-accounts xlsx template (cliente import).
   *
   * Returns the raw `Blob` so the caller can trigger a browser download. RBAC
   * (gestor+) is enforced upstream — a 403 surfaces as an AxiosError so the
   * page can show the localized toast.
   */
  downloadCommercialAccountsTemplate: async (): Promise<Blob> => {
    const { data } = await api.get<Blob>(
      '/cross-sell/templates/commercial-accounts.xlsx',
      { responseType: 'blob' },
    );
    return data;
  },

  /** Download the product catalog xlsx template. Same contract as above. */
  downloadCatalogTemplate: async (): Promise<Blob> => {
    const { data } = await api.get<Blob>('/cross-sell/templates/catalog.xlsx', {
      responseType: 'blob',
    });
    return data;
  },

  /* ── Catalog bulk import (síncrono, sem PII/LGPD) ─────────────────────────
   *
   * `POST /cross-sell/catalog/import?mode=preview|confirm` — multipart (`file`,
   * xlsx/csv ≤50MB). SÍNCRONO (sem job/SSE): `mode=preview` retorna o que será
   * criado/atualizado/ignorado por linha; `mode=confirm` aplica e retorna os
   * contadores. Espelha o multipart do import de clientes (`createImport`).
   */
  previewCatalog: async (file: File): Promise<CatalogImportPreview> => {
    const form = new FormData();
    form.append('file', file);
    const { data } = await api.post<CatalogImportPreview>(
      '/cross-sell/catalog/import?mode=preview',
      form,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    );
    return data;
  },

  confirmCatalog: async (file: File): Promise<CatalogImportResult> => {
    const form = new FormData();
    form.append('file', file);
    const { data } = await api.post<CatalogImportResult>(
      '/cross-sell/catalog/import?mode=confirm',
      form,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    );
    return data;
  },

  /* ── Whitespace ───────────────────────────────────────────────────────── */

  getAccountWhitespace: async (accountId: string): Promise<AccountWhitespace> => {
    const { data } = await api.get<AccountWhitespace>(
      `/cross-sell/accounts/${accountId}/whitespace`,
    );
    return data;
  },

  getProductWhitespace: async (productId: string): Promise<ProductWhitespace> => {
    const { data } = await api.get<ProductWhitespace>(
      `/cross-sell/products/${productId}/whitespace`,
    );
    return data;
  },

  /* ── Ranked Next-Best-Offer (WS-6/WS-7) ──────────────────────────────────
   *
   * GET /cross-sell/accounts/:id/offers — ranked NBO list. The BFF (WS-6)
   * mirrors the upstream shape VERBATIM (snake_case), so we map snake↔camel
   * here and tolerate either casing as well as a `{data:[]}` envelope or a
   * bare array. Returns [] on any odd/empty payload — the caller falls back to
   * the existing eligible-bucket rendering.
   */
  getAccountOffers: async (commercialAccountId: string): Promise<RankedOffer[]> => {
    const { data } = await api.get<unknown>(
      `/cross-sell/accounts/${commercialAccountId}/offers`,
    );
    const rows: unknown[] = Array.isArray(data)
      ? data
      : Array.isArray((data as { data?: unknown })?.data)
        ? ((data as { data: unknown[] }).data)
        : Array.isArray((data as { offers?: unknown })?.offers)
          ? ((data as { offers: unknown[] }).offers)
          : [];

    return rows
      .filter((r): r is Record<string, unknown> => !!r && typeof r === 'object')
      .map((r): RankedOffer => {
        const productId = String(r.productId ?? r.product_id ?? '');
        const productName = String(r.productName ?? r.product_name ?? '');
        const rawScore = r.score;
        const score =
          typeof rawScore === 'number'
            ? rawScore
            : typeof rawScore === 'string' && rawScore.trim() !== '' && !Number.isNaN(Number(rawScore))
              ? Number(rawScore)
              : null;
        const reasons = Array.isArray(r.reasons)
          ? (r.reasons as unknown[]).filter((x): x is string => typeof x === 'string')
          : [];
        const sku = (r.sku as string | null | undefined) ?? null;
        const components = (r.components as Record<string, unknown> | null | undefined) ?? null;
        return { productId, productName, sku, score, reasons, components };
      })
      .filter((o) => o.productId !== '' || o.productName !== '');
  },

  /* ── Exclusions ───────────────────────────────────────────────────────── */

  createExclusion: async (payload: CreateExclusionPayload): Promise<{ success: boolean }> => {
    const { data } = await api.post<{ success: boolean }>('/cross-sell/exclusions', payload);
    return data;
  },

  /** Re-include a previously excluded product (DELETE with a body). */
  deleteExclusion: async (payload: CreateExclusionPayload): Promise<{ success: boolean }> => {
    const { data } = await api.delete<{ success: boolean }>('/cross-sell/exclusions', {
      data: payload,
    });
    return data;
  },

  /* ── Campaigns ────────────────────────────────────────────────────────── */

  listCampaigns: async (): Promise<CampaignListItem[]> => {
    const { data } = await api.get<CampaignListItem[] | { data: CampaignListItem[] }>(
      '/cross-sell/campaigns',
    );
    return Array.isArray(data) ? data : (data?.data ?? []);
  },

  getCampaign: async (campaignId: string): Promise<Campaign> => {
    const { data } = await api.get<Campaign>(`/cross-sell/campaigns/${campaignId}`);
    return data;
  },

  /** Create a draft campaign (status starts as `draft`). */
  createCampaign: async (payload: CreateCampaignPayload): Promise<Campaign> => {
    const { data } = await api.post<Campaign>('/cross-sell/campaigns', payload);
    return data;
  },

  /**
   * Async reach estimate from the eligibility filters (UX §4.7 "Calcular
   * alcance"). Does NOT persist anything — returns counts only.
   */
  previewReach: async (payload: PreviewReachPayload): Promise<PreviewReach> => {
    const { data } = await api.post<PreviewReach>('/cross-sell/campaigns/preview-reach', payload);
    return data;
  },

  /**
   * Activate a draft/paused campaign: snapshots the eligible members and
   * enqueues them in sdr-deal-queue (202). Returns the new status + enqueued
   * count so the UI can surface "primeiras calls em até 5min" (F-CS-03.1).
   */
  activateCampaign: async (campaignId: string): Promise<ActivateCampaignResult> => {
    const { data } = await api.post<ActivateCampaignResult>(
      `/cross-sell/campaigns/${campaignId}/activate`,
      {},
    );
    return data;
  },

  pauseCampaign: async (campaignId: string): Promise<{ status: Campaign['status'] }> => {
    const { data } = await api.post<{ status: Campaign['status'] }>(
      `/cross-sell/campaigns/${campaignId}/pause`,
      {},
    );
    return data;
  },

  /** Cursor-paginated members, optionally filtered by status (TECH_SPEC §5.3). */
  getCampaignMembers: async (
    campaignId: string,
    opts?: { status?: CampaignMemberStatus; cursor?: string; limit?: number },
  ): Promise<CampaignMembersPage> => {
    const { data } = await api.get<CampaignMembersPage | CampaignMembersPage['data']>(
      `/cross-sell/campaigns/${campaignId}/members`,
      { params: { status: opts?.status, cursor: opts?.cursor, limit: opts?.limit } },
    );
    // Tolerate a bare array (no envelope) as well as `{data, next_cursor}`.
    return Array.isArray(data)
      ? { data, nextCursor: null }
      : { data: data?.data ?? [], nextCursor: data?.nextCursor ?? null };
  },

  getCampaignFunnel: async (campaignId: string): Promise<FunnelData> => {
    const { data } = await api.get<FunnelData>(`/cross-sell/campaigns/${campaignId}/funnel`);
    return data;
  },

  /** SDR submits the outcome at the end of a call (TECH_SPEC §5.4 — 202). */
  submitMemberOutcome: async (
    memberId: string,
    payload: SubmitOutcomePayload,
  ): Promise<SubmitOutcomeResult> => {
    const { data } = await api.post<SubmitOutcomeResult>(
      `/cross-sell/campaign-members/${memberId}/outcome`,
      payload,
    );
    return data;
  },

  /** URL for the per-campaign SSE EventSource (cookie + query-token auth). */
  campaignStreamUrl: (campaignId: string): string =>
    withSseToken(`${CROSS_SELL_SSE_BASE}/campaigns/${campaignId}/stream`),
};

export default crossSellApi;
